import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { MediaAssetSummary, MediaUploadResult, Page as ResultPage } from '@cms/contracts'
import { ApiError } from '../api/client.js'
import { listMedia, mediaFileUrl } from '../api/library.js'
import { liveUrl } from '../domain/live-url.js'
import { MEDIA_KINDS, altFor, formatBytes, mediaDisplayName, mediaKindOf, type MediaKind, type MediaStatusFilter } from '../domain/media.js'
import { pageWindow } from '../domain/format.js'
import { useI18n } from '../i18n/context.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { TextField } from '../ui/fields.js'
import type { IconName } from '../ui/icons.js'
import { Banner, Button, EmptyState, FilterChip, Icon, IconButton, Skeleton, cx } from '../ui/primitives.js'
import { MediaUploader } from './MediaUploader.js'

export interface MediaBrowserFilters {
  q: string
  kind: MediaKind | ''
  status: MediaStatusFilter
  page: number
}

/** A grid of 48 fills 2, 3, 4 and 6 columns without a ragged last row on a full page. */
export const MEDIA_PAGE_SIZE = 48

const KIND_ICONS: Record<MediaKind, IconName> = { image: 'image', document: 'picture_as_pdf', video: 'movie' }

/**
 * The library as a grid of previews, with search, kind and status filters,
 * upload and paging. The same component is the library screen (filters in
 * the URL, tiles are links to the detail) and the picker of a media field
 * (filters in the dialog, tiles select).
 */
export function MediaBrowser({ siteId, filters, onFiltersChange, showStatus, itemHref, onSelect, onActivate, selectedId, canUpload, onUploaded, reloadToken = 0 }: {
  siteId: string
  filters: MediaBrowserFilters
  onFiltersChange: (next: MediaBrowserFilters) => void
  showStatus?: boolean
  itemHref?: (asset: MediaAssetSummary) => string
  onSelect?: (asset: MediaAssetSummary) => void
  /** Double click or Enter on a tile: "use this one". */
  onActivate?: (asset: MediaAssetSummary) => void
  selectedId?: string | null
  canUpload: boolean
  onUploaded?: (result: MediaUploadResult) => void
  reloadToken?: number
}) {
  const { t, locale } = useI18n()
  const [page, setPage] = useState<ResultPage<MediaAssetSummary> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState(filters.q)
  const [uploads, setUploads] = useState(0)
  const [retry, setRetry] = useState(0)

  useEffect(() => setSearch(filters.q), [filters.q])

  useEffect(() => {
    const words = search.trim()
    if (words === filters.q) return
    const timer = window.setTimeout(() => onFiltersChange({ ...filters, q: words, page: 1 }), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listMedia(siteId, {
      q: filters.q || undefined,
      kind: filters.kind || undefined,
      status: filters.status,
      limit: MEDIA_PAGE_SIZE,
      cursor: filters.page > 1 ? String((filters.page - 1) * MEDIA_PAGE_SIZE) : undefined,
    })
      .then((result) => { if (!cancelled) setPage(result) })
      .catch((err: unknown) => {
        if (cancelled) return
        setPage(null)
        setError(err instanceof ApiError ? err.message : t('errors.network'))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [siteId, filters.q, filters.kind, filters.status, filters.page, reloadToken, uploads, retry, t])

  const uploaded = (result: MediaUploadResult) => {
    onUploaded?.(result)
    // The newest file is first in the list: back to page 1 of the active ones, of its kind, to show it.
    const kindHides = !!filters.kind && mediaKindOf(result.asset.mimeType) !== filters.kind
    if (filters.page !== 1 || filters.status === 'archived' || filters.q || kindHides) {
      onFiltersChange({ ...filters, q: '', status: filters.status === 'archived' ? 'active' : filters.status, kind: kindHides ? '' : filters.kind, page: 1 })
    }
    setUploads((count) => count + 1)
  }

  const set = (change: Partial<MediaBrowserFilters>) => onFiltersChange({ ...filters, page: 1, ...change })
  const filtered = !!(filters.q || filters.kind || filters.status !== 'active')
  const range = page ? pageWindow(filters.page, MEDIA_PAGE_SIZE, page.total) : null

  return <div className="media-browser">
    {canUpload && <MediaUploader siteId={siteId} onUploaded={uploaded} compact={!showStatus}/>}

    <div className="toolbar">
      <TextField
        className="toolbar-search"
        label={t('media.search')}
        hideLabel
        type="search"
        leadingIcon="search"
        value={search}
        placeholder={t('media.searchPlaceholder')}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); set({ q: search.trim() }) } }}
      />
      <div className="chip-row" role="group" aria-label={t('media.filters.kind')}>
        <FilterChip selected={!filters.kind} onClick={() => set({ kind: '' })}>{t('media.kinds.all')}</FilterChip>
        {MEDIA_KINDS.map((kind) => <FilterChip key={kind} selected={filters.kind === kind} icon={KIND_ICONS[kind]} onClick={() => set({ kind: filters.kind === kind ? '' : kind })}>{t(`media.kinds.${kind}`)}</FilterChip>)}
      </div>
      {showStatus && <div className="chip-row" role="group" aria-label={t('media.filters.status')}>
        {(['active', 'archived', 'all'] as const).map((status) => <FilterChip key={status} selected={filters.status === status} onClick={() => set({ status })}>{t(`media.statuses.${status}`)}</FilterChip>)}
      </div>}
    </div>

    {error && <Banner tone="error" title={t('media.loadError')} action={<Button variant="text" icon="refresh" onClick={() => setRetry((count) => count + 1)}>{t('media.retry')}</Button>}>{error}</Banner>}

    {loading && !page ? <MediaGridSkeleton label={t('common.loading')}/>
      : page && page.items.length === 0 ? <EmptyState
        icon={filtered ? 'search' : 'photo_library'}
        title={filtered ? t('media.emptyFiltered') : t('media.empty')}
        action={filtered ? <Button variant="outlined" onClick={() => onFiltersChange({ q: '', kind: '', status: 'active', page: 1 })}>{t('media.clearFilters')}</Button> : undefined}
      >{filtered ? t('media.emptyFilteredHelp') : canUpload ? t('media.emptyHelp') : t('media.emptyReadOnly')}</EmptyState>
      : page && <>
        <ul className={cx('media-grid', loading && 'is-refreshing')} aria-busy={loading}>
          {page.items.map((asset) => <li key={asset.id}>
            <MediaTile asset={asset} href={itemHref?.(asset)} selected={asset.id === selectedId} onSelect={onSelect} onActivate={onActivate}/>
          </li>)}
        </ul>
        {range && page.total > MEDIA_PAGE_SIZE && <div className="pagination">
          <span className="pagination-range">{t('pagination.range', { from: range.from, to: range.to, total: page.total })}</span>
          <IconButton icon="chevron_left" label={t('pagination.previous')} disabled={filters.page <= 1} onClick={() => onFiltersChange({ ...filters, page: filters.page - 1 })}/>
          <IconButton icon="chevron_right" label={t('pagination.next')} disabled={filters.page >= range.pageCount} onClick={() => onFiltersChange({ ...filters, page: filters.page + 1 })}/>
        </div>}
        {range && page.total <= MEDIA_PAGE_SIZE && <p className="muted media-count">{t('media.count', { count: page.total, size: formatBytes(page.items.reduce((sum, item) => sum + (item.size ?? 0), 0), locale) })}</p>}
      </>}
  </div>
}

function MediaTile({ asset, href, selected, onSelect, onActivate }: {
  asset: MediaAssetSummary
  href?: string
  selected?: boolean
  onSelect?: (asset: MediaAssetSummary) => void
  onActivate?: (asset: MediaAssetSummary) => void
}) {
  const { t, locale } = useI18n()
  const kind = mediaKindOf(asset.mimeType)
  const extension = asset.path.split('.').pop()?.toUpperCase() ?? ''
  const meta = [extension, asset.width && asset.height ? `${asset.width}×${asset.height}` : null, formatBytes(asset.size, locale)].filter(Boolean).join(' · ')
  const content = <>
    <MediaThumb asset={asset}/>
    <span className="media-tile-text">
      <strong className="media-tile-name" title={mediaDisplayName(asset)}>{mediaDisplayName(asset)}</strong>
      <small className="media-tile-meta">{meta}</small>
      {asset.status === 'archived' && <small className="media-tile-flag"><Icon name="archive" size={18}/>{t('media.statuses.archivedOne')}</small>}
      {kind === 'image' && !altFor(asset.alt, locale) && asset.status === 'active' && <small className="media-tile-flag is-warning">{t('media.noAlt')}</small>}
    </span>
  </>
  if (href) return <Link to={href} className={cx('media-tile', selected && 'is-selected')}>{content}</Link>
  return <button
    type="button"
    className={cx('media-tile', selected && 'is-selected')}
    aria-pressed={selected}
    onClick={() => onSelect?.(asset)}
    onDoubleClick={() => onActivate?.(asset)}
    onKeyDown={(event) => { if (event.key === 'Enter' && selected) { event.preventDefault(); onActivate?.(asset) } }}
  >{content}</button>
}

/**
 * The preview: the file from the library when it holds the bytes, the copy
 * on the published site for assets recorded before the library stored them,
 * an icon when neither answers.
 */
export function MediaThumb({ asset, large }: { asset: Pick<MediaAssetSummary, 'id' | 'mimeType' | 'publicPath' | 'alt' | 'sha256'>; large?: boolean }) {
  const { locale } = useI18n()
  const { site } = useSiteWorkspace()
  const kind = mediaKindOf(asset.mimeType)
  const sources = [
    asset.sha256 ? mediaFileUrl(asset.id) : null,
    liveUrl(site?.primaryDomain, asset.publicPath),
  ].filter((source): source is string => !!source)
  const [attempt, setAttempt] = useState(0)
  useEffect(() => setAttempt(0), [asset.id])
  const source = sources[attempt]
  const icon: IconName = kind === 'document' ? 'picture_as_pdf' : kind === 'video' ? 'movie' : attempt > 0 && !source ? 'broken_image' : 'image'
  return <span className={cx('media-thumb', large && 'media-thumb-large')}>
    {kind === 'image' && source
      ? <img src={source} alt={altFor(asset.alt, locale)} loading="lazy" decoding="async" onError={() => setAttempt((current) => current + 1)}/>
      : <Icon name={icon} size={large ? 40 : 32}/>}
  </span>
}

function MediaGridSkeleton({ label }: { label: string }) {
  return <ul className="media-grid" role="status" aria-label={label}>
    {Array.from({ length: 12 }, (_, index) => <li key={index} className="media-tile media-tile-skeleton" aria-hidden="true">
      <span className="media-thumb skeleton"/>
      <span className="media-tile-text"><Skeleton width="80%"/><Skeleton width="50%" height={12}/></span>
    </li>)}
  </ul>
}
